import { FullLoader } from "components/FullLoader";
import { useOnboarding } from "hooks/useOnboarding";
import { OnBoardOverview } from "pages/auth/OnBoardOverview";
import { SignupAttribution } from "pages/auth/signupAttribution/SignupAttribution";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "store/auth.store";
import { auth } from "utils/firebase";
import { getActAsGrantId } from "utils/supportAccess";
import { AppLayout } from "./AppLayout";

export function Protected() {
	const isAuth = useAuthStore((state) => !!state.uid);
	const [fbUser, setFbUser] = useState<any>(null);
	// Once the user has fully entered the app, keep AppLayout mounted across
	// transient auth/onboarding revalidations (window-focus token refresh, SWR
	// refetches after connecting a bank, etc.). Without this, a brief `!fbUser`
	// or `onboardingLoading` flip swaps AppLayout for <FullLoader/>, unmounting
	// the entire tree — which reset in-progress UI like the Add Company
	// drawer/wizard whenever the user tabbed away (e.g. to the QBO/Plaid popup)
	// and came back.
	const [enteredApp, setEnteredApp] = useState(false);
	const {
		onboardingCompleted,
		attributionRequired,
		settled: onboardingSettled,
		isLoading: onboardingLoading,
	} = useOnboarding(false);
	// "How did you find us?" (EQ-2266). Never inside a staff support-access
	// session: the answer write is refused there and staff would be stuck.
	const askAttribution = attributionRequired && !getActAsGrantId();

	const location = useLocation();
	// Encode so the target's own query params (e.g. the chrome plugin's
	// accountId/from/to) aren't truncated at the first "&" when SignIn reads
	// returnUrl back out of the URL.
	const signInUrl = `/signin?returnUrl=${encodeURIComponent(
		location.pathname + location.search,
	)}`;

	useEffect(() => {
		if (!isAuth) return;
		const unsubscribe = auth.onAuthStateChanged((user) => {
			setFbUser(user);
		});

		return () => unsubscribe();
	}, []);

	useEffect(() => {
		if (
			isAuth &&
			fbUser?.emailVerified &&
			!onboardingLoading &&
			onboardingCompleted !== false &&
			!askAttribution
		) {
			setEnteredApp(true);
		}
	}, [isAuth, fbUser, onboardingLoading, onboardingCompleted, askAttribution]);

	// A real logout still tears the app down (checked first). Everything past
	// that is sticky once entered, so transient revalidation flips don't unmount
	// AppLayout and reset in-progress UI.
	if (!isAuth) return <Navigate to={signInUrl} />;
	if (enteredApp) return <AppLayout />;
	if (!fbUser) return <FullLoader />;
	// Wait for the server before leaving for /verify-email: that route is
	// outside this component, so a redirect taken before the answer arrives
	// would skip the question for every password sign-up.
	if (!onboardingSettled) return <FullLoader />;
	if (askAttribution) return <SignupAttribution />;
	if (!fbUser.emailVerified) return <Navigate to={"verify-email"} />;
	if (onboardingLoading) return <FullLoader />;
	if (onboardingCompleted === false) return <OnBoardOverview />;

	return <AppLayout></AppLayout>;
}
